import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Search, Check, X, Loader2, Briefcase, AlertCircle, ShieldCheck, Sparkles,
} from "lucide-react";
import { API } from "../apiBase";
const RX = /^[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/;

/**
 * Public domain-availability widget against the official ANS Liste Blanche.
 * Used as a lead-gen tool on the MSSanté landing pages.
 *
 * UI: [your-name] @ [______________] .mssante.fr  →  ✓ Disponible / ✗ Pris
 * If available → CTA "Réserver ce sous-domaine" (mailto pré-rempli)
 * If taken    → message + suggestion + CTA "Demander un autre"
 */
export default function MssanteDomainCheck() {
  const [name, setName] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const debounceRef = useRef(null);

  const trimmed = name.toLowerCase().replace(/[^a-z0-9-]/g, "").replace(/^-+|-+$/g, "");
  const valid = RX.test(trimmed);

  useEffect(() => {
    if (!valid) {
      setResult(null);
      setErr("");
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setBusy(true);
      setErr("");
      try {
        const { data } = await axios.post(`${API}/mssante/check-domain`, { name: trimmed });
        setResult(data);
      } catch (e) {
        setResult(null);
        setErr(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Vérification impossible.");
      } finally {
        setBusy(false);
      }
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [trimmed, valid]);

  const mailtoQuote = (subdomain) =>
    `mailto:contact@metaconnect.fr?subject=${encodeURIComponent(
      `Réservation sous-domaine MSSanté — ${subdomain}.mssante.fr`,
    )}&body=${encodeURIComponent(
      `Bonjour,\n\nJe souhaite réserver le sous-domaine MSSanté : ${subdomain}.mssante.fr\n\nEnseigne / Groupe : [Nom]\nSIRET : [Numéro]\nNombre de sites : [X]\n\nPourriez-vous me faire parvenir un devis ?\n\nMerci,`,
    )}`;

  return (
    <section className="hex-soft py-14 lg:py-20" data-testid="domain-check-section">
      <div className="max-w-4xl mx-auto px-5 lg:px-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[var(--mc-mint)] mb-3">
            <Sparkles size={13} /> Outil exclusif · Liste blanche officielle ANS
          </div>
          <h2 className="font-display text-[#0F2B5B] text-3xl sm:text-4xl font-semibold leading-tight">
            Vérifiez la disponibilité de votre sous-domaine MSSanté
          </h2>
          <p className="mt-3 text-[14px] text-[#64748B] max-w-2xl mx-auto leading-relaxed">
            Tapez le nom souhaité — nous interrogeons en direct la <strong className="text-[#0F2B5B]">liste blanche officielle de l'Agence du Numérique en Santé</strong> pour vous dire s'il est libre.
          </p>
        </div>

        <div className="mc-card p-6 lg:p-8">
          {/* Input row */}
          <label className="block text-[11px] uppercase tracking-[0.18em] text-[#475569] mb-3">
            Votre adresse MSSanté ressemblerait à :
          </label>
          <div className="flex items-stretch gap-2 flex-wrap" data-testid="domain-check-input-row">
            <div className="flex-1 min-w-[260px] flex items-stretch rounded-lg border border-[rgba(127,179,255,0.32)] bg-[#F1F5F9] overflow-hidden focus-within:border-[var(--mc-blue-400)] transition">
              <span className="px-3 py-3 text-[13px] text-[#64748B] font-mono select-none border-r border-[rgba(127,179,255,0.18)]">
                etablissement@
              </span>
              <input data-testid="domain-check-input"
                     value={name}
                     onChange={(e) => setName(e.target.value.toLowerCase().slice(0, 63))}
                     placeholder="votre-enseigne"
                     autoComplete="off" autoCorrect="off" autoCapitalize="none" spellCheck="false"
                     className="flex-1 px-3 py-3 bg-transparent text-[#0F2B5B] font-mono text-[14px] focus:outline-none placeholder:text-[#64748B]" />
              <span className="px-3 py-3 text-[13px] text-[#64748B] font-mono select-none border-l border-[rgba(127,179,255,0.18)]">
                .mssante.fr
              </span>
            </div>
            <div className="grid place-items-center px-4 min-w-[120px] rounded-lg border border-[rgba(127,179,255,0.20)] bg-[rgba(45,123,255,0.04)] text-[12px] text-[#64748B]"
                 data-testid="domain-check-status">
              {!trimmed && <><Search size={14} className="inline mr-1.5" /> En attente</>}
              {trimmed && !valid && <><AlertCircle size={14} className="inline mr-1.5 text-[#B45309]" /> Invalide</>}
              {valid && busy && <><Loader2 size={14} className="inline mr-1.5 animate-spin" /> Vérification…</>}
              {valid && !busy && result?.available && (
                <span className="text-[var(--mc-mint)]"><Check size={14} className="inline mr-1.5" /> Disponible</span>
              )}
              {valid && !busy && result && !result.available && (
                <span className="text-[#FFB4B4]"><X size={14} className="inline mr-1.5" /> Pris</span>
              )}
            </div>
          </div>

          {/* Hints */}
          {trimmed && !valid && (
            <p className="mt-3 text-[12px] text-[#B45309] flex items-start gap-2">
              <AlertCircle size={13} className="mt-0.5 shrink-0" />
              Lettres minuscules, chiffres et tirets uniquement. 2 à 63 caractères, sans tiret en début/fin.
            </p>
          )}
          {err && (
            <p className="mt-3 text-[12px] text-[#FFB4B4] flex items-start gap-2">
              <AlertCircle size={13} className="mt-0.5 shrink-0" /> {err}
            </p>
          )}

          {/* Result block */}
          {valid && result && !busy && (
            <ResultPanel result={result} mailto={mailtoQuote(result.name)} />
          )}

          {/* Footer note */}
          <div className="mt-5 flex items-center gap-2 text-[11px] text-[#64748B] flex-wrap">
            <ShieldCheck size={11} />
            <span>
              Source officielle :{" "}
              <a href="https://espacedeconfiance.mssante.fr/listeblanchemssante.xml"
                 target="_blank" rel="noopener noreferrer"
                 className="text-[#0284C7] hover:underline">liste blanche MSSanté</a>
              {result?.last_refreshed_at && (
                <> · cache mis à jour le {new Date(result.last_refreshed_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</>
              )}
              {result?.domain_count > 0 && <> · {result.domain_count.toLocaleString("fr-FR")} domaines indexés</>}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

function ResultPanel({ result, mailto }) {
  if (result.available) {
    return (
      <div className="mt-5 rounded-xl border border-[rgba(110,231,197,0.40)] bg-[rgba(110,231,197,0.06)] p-5"
           data-testid="domain-check-result-available">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="grid place-items-center h-10 w-10 rounded-full bg-[rgba(110,231,197,0.18)] border border-[rgba(110,231,197,0.5)] text-[var(--mc-mint)] shrink-0">
            <Check size={18} />
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="text-[#0F2B5B] font-medium inline-flex items-center gap-2 flex-wrap">
              <code className="font-mono text-[var(--mc-mint)] text-[15px]">{result.full_domain}</code>
              <span className="text-[11px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border border-[rgba(110,231,197,0.5)] text-[var(--mc-mint)]">
                Disponible
              </span>
            </div>
            <p className="mt-1.5 text-[12.5px] text-[#475569] leading-relaxed">
              Ce sous-domaine n'est <strong>pas encore enregistré</strong> dans la liste blanche officielle. En tant qu'opérateur MSSanté, MetaConnect peut le réserver pour votre enseigne ou votre groupe.
            </p>
            <p className="mt-1 text-[11px] text-[#64748B]">
              Réservation auprès de l'ANS · paramétrage · déploiement multi-sites · 2 à 4 semaines
            </p>
          </div>
          <a href={mailto} data-testid="domain-check-cta-reserve"
             className="btn-primary px-5 py-2.5 rounded-full text-[12.5px] font-medium inline-flex items-center gap-1.5 shrink-0">
            <Briefcase size={13} /> Demander un devis
          </a>
        </div>
      </div>
    );
  }
  return (
    <div className="mt-5 rounded-xl border border-[rgba(255,180,80,0.40)] bg-[rgba(255,180,80,0.04)] p-5"
         data-testid="domain-check-result-taken">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="grid place-items-center h-10 w-10 rounded-full bg-[rgba(255,180,80,0.14)] border border-[rgba(255,180,80,0.45)] text-[#B45309] shrink-0">
          <X size={18} />
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="text-[#0F2B5B] font-medium inline-flex items-center gap-2 flex-wrap">
            <code className="font-mono text-[#B45309] text-[15px]">{result.full_domain}</code>
            <span className="text-[11px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border border-[rgba(255,180,80,0.45)] text-[#B45309]">
              Déjà pris
            </span>
          </div>
          <p className="mt-1.5 text-[12.5px] text-[#64748B] leading-relaxed">
            Ce sous-domaine est déjà enregistré dans la liste blanche MSSanté
            {result.matched_description && <> par <strong className="text-[#475569]">« {result.matched_description} »</strong></>}.
            Essayez une variante (suffixe ville, sigle, etc.) ou laissez-nous vous proposer des alternatives.
          </p>
        </div>
        <a href="mailto:contact@metaconnect.fr?subject=Sous-domaine%20MSSant%C3%A9%20pris%20—%20alternatives"
           data-testid="domain-check-cta-alternatives"
           className="btn-ghost px-5 py-2.5 rounded-full text-[12.5px] font-medium inline-flex items-center gap-1.5 shrink-0">
          Proposer des alternatives
        </a>
      </div>
    </div>
  );
}
